import React, { useState } from 'react';
import { AlertTriangle, ChevronRight } from 'lucide-react';

interface WarningBannerProps {
  warnings: string[];
  title?: string;
}

/**
 * Amber-styled warning banner shown when analysis completed with partial results
 * or non-critical issues (e.g. some documents failed extraction, citations not linked).
 */
export const WarningBanner: React.FC<WarningBannerProps> = ({
  warnings,
  title = 'Analysis completed with warnings',
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!warnings || warnings.length === 0) return null;

  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 space-y-3">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="space-y-2 flex-1">
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1.5 group cursor-pointer w-full text-left"
          >
            <h3 className="font-semibold text-amber-900 dark:text-amber-200 text-base">
              {title}
            </h3>
            <span className="text-sm text-amber-600 dark:text-amber-400">
              ({warnings.length})
            </span>
            <ChevronRight
              className={`w-4 h-4 text-amber-600 dark:text-amber-400 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
            />
          </button>
          {expanded && (
            <>
              <ul className="space-y-1.5">
                {warnings.map((warning, i) => (
                  <li key={i} className="text-sm text-amber-800 dark:text-amber-300 leading-relaxed list-disc ml-4">
                    {warning}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-amber-700 dark:text-amber-400 leading-relaxed">
                We apologise for the inconvenience. The affected documents will be automatically
                re-processed within 24–48 hours. If you need results sooner, you can re-run the
                analysis from the case page, or{' '}
                <a
                  href={`mailto:support@clinlexis.co.uk?subject=${encodeURIComponent('Analysis Warning')}&body=${encodeURIComponent('An analysis completed with warnings. Please investigate.')}`}
                  className="font-medium underline hover:text-amber-900 dark:hover:text-amber-200"
                >
                  contact support
                </a>
                .
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
